import { useCallback, type KeyboardEvent } from 'react';
import { Icon } from '@/ui/icons/Icon';
import { cx } from './cx';
import { tabId, tabPanelId } from './tabIds';
import styles from './Tabs.module.css';

export interface TabItem<Id extends string> {
  readonly id: Id;
  readonly label: string;
  readonly icon?: string;
  /** Longer explanation shown as a native tooltip. */
  readonly description?: string;
}

interface TabProps {
  readonly id: string;
  readonly panelId: string;
  readonly label: string;
  readonly icon?: string;
  readonly description?: string;
  readonly selected: boolean;
  readonly variant: 'bar' | 'mode';
  readonly onSelect: () => void;
}

export function Tab({
  id,
  panelId,
  label,
  icon,
  description,
  selected,
  variant,
  onSelect,
}: TabProps) {
  return (
    <button
      type="button"
      role="tab"
      id={id}
      aria-selected={selected}
      aria-controls={panelId}
      tabIndex={selected ? 0 : -1}
      {...(description !== undefined ? { title: description } : {})}
      className={cx(styles.tab, styles[variant], selected && styles.selected)}
      onClick={onSelect}
    >
      {icon !== undefined && <Icon name={icon} size={variant === 'mode' ? 20 : 16} />}
      <span className={styles.label}>{label}</span>
    </button>
  );
}

interface TabsProps<Id extends string> {
  readonly tabs: readonly TabItem<Id>[];
  readonly activeId: Id;
  readonly onChange: (id: Id) => void;
  /** Accessible name of the tab list. */
  readonly label: string;
  /** Unique prefix; used to link tabs with their `tabpanel` (see `tabId`/`tabPanelId`). */
  readonly idPrefix: string;
  readonly variant?: 'bar' | 'mode';
}

/**
 * Accessible tab list (WAI-ARIA tabs pattern): roving tabindex, arrow keys, Home/End,
 * automatic activation. The consumer renders the matching `role="tabpanel"` element.
 */
export function Tabs<Id extends string>({
  tabs,
  activeId,
  onChange,
  label,
  idPrefix,
  variant = 'bar',
}: TabsProps<Id>) {
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const index = tabs.findIndex((tab) => tab.id === activeId);
      let next: number;
      switch (event.key) {
        case 'ArrowRight':
          next = (index + 1) % tabs.length;
          break;
        case 'ArrowLeft':
          next = (index - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = tabs.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      const target = tabs[next];
      if (!target) return;
      onChange(target.id);
      document.getElementById(tabId(idPrefix, target.id))?.focus();
    },
    [tabs, activeId, onChange, idPrefix],
  );

  return (
    <div
      role="tablist"
      aria-label={label}
      className={cx(styles.list, styles[`${variant}List`])}
      onKeyDown={onKeyDown}
    >
      {tabs.map((tab) => (
        <Tab
          key={tab.id}
          id={tabId(idPrefix, tab.id)}
          panelId={tabPanelId(idPrefix, tab.id)}
          label={tab.label}
          selected={tab.id === activeId}
          variant={variant}
          onSelect={() => onChange(tab.id)}
          {...(tab.icon !== undefined ? { icon: tab.icon } : {})}
          {...(tab.description !== undefined ? { description: tab.description } : {})}
        />
      ))}
    </div>
  );
}
